function buildSuggestions(question) {
  const q = question.toLowerCase()
  if (q.includes("career")) {
    return ["Should I switch jobs?", "How should I negotiate my next raise?", "What month looks strongest?"]
  }
  if (q.includes("relationship") || q.includes("marriage")) {
    return ["Is this the right person?", "What should I ask next?", "When does it stabilize?"]
  }
  if (q.includes("finance")) {
    return ["Should I invest now?", "What should I avoid?", "What is the safer timing window?"]
  }
  return ["What is the strongest next step?", "Can you narrow the timing window?", "What should I watch for?"]
}

function buildPendingTurn(question, createdAt) {
  return {
    id: `pending-${createdAt ?? Date.now().toString(36)}`,
    question,
    answer: "",
    detailedAnswer: "",
    reasoning: "",
    sources: [],
    suggestions: buildSuggestions(question),
    status: "thinking",
    progressMessages: [],
    cancelRequested: false,
    amountDebitedPaise: null,
    availableBalancePaise: null,
    createdAt,
  }
}

function buildActiveTurnFromServer(question, activeTurn, requestMessageId, createdAt, localTurn) {
  const localLivePhase =
    localTurn?.livePhase === "thinking" ||
    localTurn?.livePhase === "streaming" ||
    localTurn?.livePhase === "settling"
      ? localTurn.livePhase
      : undefined
  const localHasPartialAnswer = Boolean(
    (typeof localTurn?.answer === "string" && localTurn.answer.trim()) ||
      (typeof localTurn?.detailedAnswer === "string" && localTurn.detailedAnswer.trim())
  )
  const livePhase =
    activeTurn.status === "settling"
      ? "settling"
      : localLivePhase ??
        (localHasPartialAnswer ? "streaming" : "thinking")
  return {
    id: activeTurn.turn_id,
    question,
    answer: localTurn?.answer ?? "",
    detailedAnswer: localTurn?.detailedAnswer ?? "",
    reasoning: localTurn?.reasoning ?? "",
    sources: localTurn?.sources ?? [],
    suggestions: localTurn?.suggestions ?? buildSuggestions(question),
    status: livePhase === "thinking" ? "thinking" : "streaming",
    livePhase,
    serverTurnId: activeTurn.turn_id,
    requestMessageId,
    retryOfTurnId: activeTurn.retry_of_turn_id ?? undefined,
    progressMessages:
      livePhase === "thinking" ? localTurn?.progressMessages ?? [] : [],
    cancelRequested: livePhase === "settling" ? false : localTurn?.cancelRequested ?? false,
    retryCount: activeTurn.retry_count ?? localTurn?.retryCount ?? 0,
    amountDebitedPaise:
      typeof activeTurn.amount_debited_paise === "number"
        ? activeTurn.amount_debited_paise
        : localTurn?.amountDebitedPaise ?? null,
    availableBalancePaise: localTurn?.availableBalancePaise ?? null,
    createdAt: createdAt ?? activeTurn.started_at ?? localTurn?.createdAt,
  }
}

function buildFailedTurnFromServer(question, failedTurn, requestMessageId, createdAt) {
  return {
    id: failedTurn.turn_id,
    question,
    answer: "",
    detailedAnswer: "",
    reasoning: "",
    sources: [],
    suggestions: buildSuggestions(question),
    status: "failed",
    livePhase: undefined,
    serverTurnId: failedTurn.turn_id,
    requestMessageId,
    retryOfTurnId: failedTurn.retry_of_turn_id ?? undefined,
    progressMessages: [],
    cancelRequested: false,
    errorCode: failedTurn.error_code,
    errorMessage: failedTurn.error_message,
    retryable: failedTurn.retryable,
    retryCount: failedTurn.retry_count,
    amountDebitedPaise:
      typeof failedTurn.amount_debited_paise === "number"
        ? failedTurn.amount_debited_paise
        : null,
    followUpState: failedTurn.follow_up_state ?? undefined,
    followUpTurnId: failedTurn.follow_up_turn_id ?? undefined,
    followUpConversationId: failedTurn.follow_up_conversation_id ?? undefined,
    availableBalancePaise: null,
    createdAt,
  }
}

export function mergeRefreshedConversation(
  currentConversation,
  responseData,
  items,
  turnsByRequestMessageId
) {
  const turns = []
  let currentQuestion = ""
  let currentRequestMessageId
  let currentQuestionCreatedAt
  const localTurns = currentConversation?.turns ?? []
  const localAssistantFeedbackByMessageId = new Map(
    (currentConversation?.turns ?? [])
      .filter((turn) => typeof turn.answerMessageId === "string" && turn.assistantFeedback != null)
      .map((turn) => [turn.answerMessageId, turn.assistantFeedback])
  )
  const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

  function findLocalLiveTurn(requestMessageId, question) {
    return (
      localTurns.find(
        (turn) =>
          (turn.status === "thinking" || turn.status === "streaming") &&
          ((requestMessageId && turn.requestMessageId === requestMessageId) || turn.question === question)
      ) ?? null
    )
  }

  function pushActiveTurnFromServer(question, requestMessageId, createdAt) {
    if (!requestMessageId) {
      return false
    }
    const serverTurn = turnsByRequestMessageId?.get(requestMessageId)
    if (!serverTurn || !["queued", "running", "settling"].includes(serverTurn.status)) {
      return false
    }
    turns.push(
      buildActiveTurnFromServer(
        question,
        serverTurn,
        requestMessageId,
        createdAt,
        findLocalLiveTurn(requestMessageId, question)
      )
    )
    return true
  }

  function pushFailedTurnFromServer(question, requestMessageId, createdAt) {
    if (!requestMessageId) {
      return false
    }
    const serverTurn = turnsByRequestMessageId?.get(requestMessageId)
    if (!serverTurn || serverTurn.status !== "failed") {
      return false
    }
    turns.push(buildFailedTurnFromServer(question, serverTurn, requestMessageId, createdAt))
    return true
  }

  for (const item of items) {
    if (item.role === "user" && item.message_type === "message") {
      if (currentQuestion) {
        if (!pushFailedTurnFromServer(currentQuestion, currentRequestMessageId, currentQuestionCreatedAt)) {
          pushActiveTurnFromServer(currentQuestion, currentRequestMessageId, currentQuestionCreatedAt)
        }
      }
      currentQuestion = item.content
      currentRequestMessageId = item.message_id
      currentQuestionCreatedAt = item.created_at
      continue
    }

    if (item.role === "assistant" && item.message_type === "message") {
      const completedTurn = {
        id: item.message_id,
        question: currentQuestion || item.content,
        answer: item.plain_answer ?? item.content,
        detailedAnswer: item.detailed_answer ?? "",
        reasoning: "",
        sources: [],
        suggestions: item.follow_up_suggestions ?? [],
        status: "complete",
        serverTurnId: item.message_id,
        requestMessageId: currentRequestMessageId ?? item.message_id,
        answerMessageId: item.message_id,
        assistantFeedback:
          hasOwn(item, "assistant_feedback")
            ? (item.assistant_feedback ?? null)
            : localAssistantFeedbackByMessageId.get(item.message_id) ?? null,
        progressMessages: [],
        amountDebitedPaise: item.amount_debited_paise,
        availableBalancePaise: null,
        createdAt: currentQuestionCreatedAt ?? item.created_at,
      }

      if (currentQuestion || turns.length === 0) {
        turns.push(completedTurn)
      } else {
        const previousTurn = turns[turns.length - 1]
        turns[turns.length - 1] = {
          ...previousTurn,
          answer: completedTurn.answer,
          detailedAnswer: completedTurn.detailedAnswer,
          suggestions: completedTurn.suggestions,
          status: "complete",
          serverTurnId: completedTurn.serverTurnId,
          requestMessageId: previousTurn.requestMessageId ?? completedTurn.requestMessageId,
          answerMessageId: completedTurn.answerMessageId,
          assistantFeedback: completedTurn.assistantFeedback,
          progressMessages: [],
          amountDebitedPaise: completedTurn.amountDebitedPaise,
          availableBalancePaise: null,
        }
      }
      currentQuestion = ""
      currentRequestMessageId = undefined
      currentQuestionCreatedAt = undefined
    }
  }

  if (
    currentQuestion &&
    pushFailedTurnFromServer(currentQuestion, currentRequestMessageId, currentQuestionCreatedAt)
  ) {
    currentQuestion = ""
    currentRequestMessageId = undefined
    currentQuestionCreatedAt = undefined
  } else if (
    currentQuestion &&
    pushActiveTurnFromServer(currentQuestion, currentRequestMessageId, currentQuestionCreatedAt)
  ) {
    currentQuestion = ""
    currentRequestMessageId = undefined
    currentQuestionCreatedAt = undefined
  }

  const failedTurnFollowUpByKey = new Map()
  for (const localTurn of localTurns) {
    if (
      localTurn.status !== "failed" ||
      (!localTurn.followUpState && !localTurn.followUpActionState)
    ) {
      continue
    }
    const keys = [localTurn.requestMessageId, localTurn.serverTurnId, localTurn.id]
    for (const key of keys) {
      if (typeof key === "string" && key.trim()) {
        failedTurnFollowUpByKey.set(key, {
          followUpState: localTurn.followUpState,
          followUpTurnId: localTurn.followUpTurnId,
          followUpConversationId: localTurn.followUpConversationId,
          followUpActionState: localTurn.followUpActionState,
        })
      }
    }
  }
  for (let index = 0; index < turns.length; index += 1) {
    const turn = turns[index]
    if (turn.status !== "failed") {
      continue
    }
    const preserved =
      (turn.requestMessageId ? failedTurnFollowUpByKey.get(turn.requestMessageId) : undefined) ??
      (turn.serverTurnId ? failedTurnFollowUpByKey.get(turn.serverTurnId) : undefined) ??
      failedTurnFollowUpByKey.get(turn.id)
    const hasServerFollowUp =
      Boolean(turn.followUpState) ||
      Boolean(turn.followUpTurnId) ||
      Boolean(turn.followUpConversationId)
    const nextFollowUpState = hasServerFollowUp ? turn.followUpState : preserved?.followUpState
    const nextFollowUpTurnId = hasServerFollowUp ? turn.followUpTurnId : preserved?.followUpTurnId
    const nextFollowUpConversationId = hasServerFollowUp
      ? turn.followUpConversationId
      : preserved?.followUpConversationId
    const nextFollowUpActionState = hasServerFollowUp ? undefined : preserved?.followUpActionState
    if (!nextFollowUpState && !nextFollowUpActionState) {
      continue
    }
    turns[index] = {
      ...turn,
      followUpState: nextFollowUpState,
      followUpTurnId: nextFollowUpTurnId,
      followUpConversationId: nextFollowUpConversationId,
      followUpActionState: nextFollowUpActionState,
    }
  }

  const pendingTurn =
    localTurns.find((turn) => turn.status === "thinking" || turn.status === "streaming") ?? null
  const failedTurn = localTurns.find((turn) => turn.status === "failed") ?? null
  const completeTurn = [...localTurns].reverse().find((turn) => turn.status === "complete") ?? null

  if (currentQuestion) {
    if (pendingTurn) {
      turns.push(pendingTurn)
    } else if (failedTurn) {
      turns.push({
        ...failedTurn,
        question: currentQuestion,
      })
    } else {
      turns.push(buildPendingTurn(currentQuestion, currentQuestionCreatedAt))
    }
  } else if (turns.length === 0 && pendingTurn) {
    turns.push(pendingTurn)
  } else if (turns.length === 0 && failedTurn) {
    turns.push(failedTurn)
  } else if (pendingTurn && turns.length < (currentConversation?.turns.length ?? 0)) {
    turns.push(pendingTurn)
  } else if (failedTurn && turns.length < localTurns.length) {
    turns.push(failedTurn)
  } else if (turns.length === 0 && completeTurn) {
    turns.push(completeTurn)
  }

  const lastItem = items.at(-1) ?? null
  const conversationTitle =
    items.find((item) => item.role === "user" && item.message_type === "message")?.content ?? "Conversation"

  return {
    ...(currentConversation ?? {}),
    personId: responseData.subject_id,
    astrologerId: responseData.astrologer_id ?? currentConversation?.astrologerId ?? "",
    astrologerDisplayName:
      responseData.astrologer_display_name ?? currentConversation?.astrologerDisplayName ?? null,
    astrologerAvatarUrl:
      responseData.astrologer_avatar_url ?? currentConversation?.astrologerAvatarUrl ?? null,
    astrologerSlug: responseData.astrologer_slug ?? currentConversation?.astrologerSlug ?? null,
    sourceFailedTurnId:
      responseData.source_failed_turn_id ?? currentConversation?.sourceFailedTurnId ?? undefined,
    title: currentConversation?.title ?? conversationTitle,
    summary: currentConversation?.summary ?? conversationTitle,
    turns,
    updatedAt: lastItem?.created_at ?? currentConversation?.updatedAt ?? "",
  }
}
